import { useAppSelector } from "../../../hooks/useAppSelector";
import { Roles } from "../../../types/roles";
import { ProjectStatus } from "../../../interfaces/utilities";
import {hasValidBuildingPermit} from "../../../utils/helpers-for-utilities";
import {getStageKey} from "../../../services/reducers/utilities-journal";

export type LockConfig = {
    designStageImport?: boolean;
    asBuiltStageImport?: boolean;
    saveAndVerifyButton?: boolean;
    approveButton?: boolean;
    rejectButton?: boolean;
    drawingToolsButton?: boolean;
    hideDeleteFilesDesign?: boolean;
    hideDeleteFilesAsBuilt?: boolean;
    hideDeleteTerritoryDesign?: boolean;
    hideDeleteTerritoryAsBuilt?: boolean;
    hideDepositPayedRow?: boolean;
};

interface RuntimeFlags {
    hasFiles: boolean;
    hasStageTerritory: boolean;
    hasBuildingTerritory: boolean;
    buildingPermitValid: boolean;
    hasUploadedFiles: boolean;
}

interface LockContext extends RuntimeFlags {
    role: Roles;
    stage: "Design Stage" | "As-Built";
    status: ProjectStatus;
    workPermitExpired: boolean;
}


const st = ProjectStatus;
const needApprovalStatuses = [st.NeedApproval, st.UnderReview];
const inactiveStatuses = [st.NotInitiated, st.Rejected, st.Approved];

export const isDesign = (ctx: LockContext) => ctx.stage === "Design Stage";
export const isAsBuilt = (ctx: LockContext) => ctx.stage === "As-Built";

type LockRule = {
    when?: (ctx: LockContext) => boolean;
    lock: LockConfig;
};

const contractorRules: LockRule[] = [
    { lock: { hideDepositPayedRow: true } },

    {
        when: ctx => !ctx.hasUploadedFiles,
        lock: { drawingToolsButton: true },
    },
    {
        when: ctx => ctx.workPermitExpired,
        lock: {
            designStageImport: true,
            asBuiltStageImport: true,
            saveAndVerifyButton: true,
            hideDeleteFilesDesign: true,
            hideDeleteFilesAsBuilt: true,
            drawingToolsButton: true,
            hideDeleteTerritoryDesign: true,
            hideDeleteTerritoryAsBuilt: true,
        },
    },
    {
        when: ctx =>
            [st.NotInitiated, st.Rejected].includes(ctx.status) && !ctx.hasFiles,
        lock: { drawingToolsButton: true },
    },
    {
        when: ctx =>
            [st.NotInitiated, st.Rejected].includes(ctx.status) &&
            (!ctx.hasStageTerritory || (ctx.buildingPermitValid && !ctx.hasBuildingTerritory)),
        lock: { saveAndVerifyButton: true },
    },
    {
        when: ctx => isAsBuilt(ctx) && ctx.status === st.NotInitiated,
        lock: {
            designStageImport: true,
            hideDeleteFilesDesign: true,
            hideDeleteTerritoryDesign: true,
        },
    },
    {
        when: ctx => isAsBuilt(ctx) && ctx.status === st.Rejected,
        lock: {
            designStageImport: true,
            hideDeleteFilesDesign: true,
            hideDeleteTerritoryDesign: true,
        },
    },
    {
        when: ctx => needApprovalStatuses.includes(ctx.status),
        lock: {
            designStageImport: true,
            asBuiltStageImport: true,
            saveAndVerifyButton: true,
            hideDeleteFilesDesign: true,
            hideDeleteFilesAsBuilt: true,
            drawingToolsButton: true,
            hideDeleteTerritoryDesign: true,
            hideDeleteTerritoryAsBuilt: true,
        },
    },
    {
        when: ctx => ctx.status === st.Approved,
        lock: {
            designStageImport: true,
            asBuiltStageImport: true,
            saveAndVerifyButton: true,
            hideDeleteFilesDesign: true,
            hideDeleteFilesAsBuilt: true,
            hideDeleteTerritoryDesign: true,
            hideDeleteTerritoryAsBuilt: true,
            drawingToolsButton: true,
        },
    },
    {
        when: ctx => isDesign(ctx) && ctx.status === st.NotInitiated,
        lock: { asBuiltStageImport: true },
    },
    {
        when: ctx => isDesign(ctx) && needApprovalStatuses.includes(ctx.status),
        lock: { designStageImport: true, asBuiltStageImport: true },
    },
    {
        when: ctx => isDesign(ctx) && ctx.status === st.Rejected,
        lock: { asBuiltStageImport: true },
    },
    {
        when: ctx => isAsBuilt(ctx) && needApprovalStatuses.includes(ctx.status),
        lock: { designStageImport: true, asBuiltStageImport: true },
    },
    {
        when: ctx => isAsBuilt(ctx) && ctx.status === st.Rejected,
        lock: { designStageImport: true },
    },
];

const specialistRules: LockRule[] = [
    {
        when: ctx =>
            needApprovalStatuses.includes(ctx.status) &&
            (!ctx.hasStageTerritory || (ctx.buildingPermitValid && !ctx.hasBuildingTerritory)),
        lock: {
            approveButton: true,
            rejectButton: true,
        },
    },
    { lock: { hideDepositPayedRow: false } },

    {
        when: ctx => !ctx.hasUploadedFiles,
        lock: { drawingToolsButton: true },
    },
    {
        when: ctx => ctx.workPermitExpired && inactiveStatuses.includes(ctx.status),
        lock: {
            approveButton: true,
            rejectButton: true,
            designStageImport: true,
            asBuiltStageImport: true,
        },
    },
    {
        when: ctx => isDesign(ctx) && ctx.status === st.NotInitiated,
        lock: {
            approveButton: true,
            rejectButton: true,
            designStageImport: true,
            asBuiltStageImport: true,
            drawingToolsButton: true,
        },
    },
    {
        when: ctx => isDesign(ctx) && needApprovalStatuses.includes(ctx.status),
        lock: { asBuiltStageImport: true },
    },

    {
        when: ctx => isAsBuilt(ctx) && needApprovalStatuses.includes(ctx.status),
        lock: {
            saveAndVerifyButton: true,
            designStageImport: true,
            hideDeleteFilesDesign: true,
            hideDeleteTerritoryDesign: true,
        },
    },
    {
        when: ctx => isAsBuilt(ctx) && ctx.status === st.NotInitiated,
        lock: {
            approveButton: true,
            rejectButton: true,
            designStageImport: true,
            asBuiltStageImport: true,
            drawingToolsButton: true,
            hideDeleteFilesDesign: true,
            hideDeleteTerritoryDesign: true,
        },
    },
    {
        when: ctx => ctx.status === st.Rejected,
        lock: {
            approveButton: true,
            rejectButton: true,
            designStageImport: true,
            asBuiltStageImport: true,
            hideDeleteFilesDesign: true,
            hideDeleteFilesAsBuilt: true,
            hideDeleteTerritoryDesign: true,
            hideDeleteTerritoryAsBuilt: true,
            drawingToolsButton: true,
        },
    },
    {
        when: ctx => ctx.status === st.Approved,
        lock: {
            approveButton: true,
            rejectButton: true,
            designStageImport: true,
            asBuiltStageImport: true,
            hideDeleteFilesDesign: true,
            hideDeleteFilesAsBuilt: true,
            hideDeleteTerritoryDesign: true,
            hideDeleteTerritoryAsBuilt: true,
            drawingToolsButton: true,
        },
    },
];

const applyRules = (rules: LockRule[], ctx: LockContext): LockConfig =>
    rules.reduce<LockConfig>((acc, rule) => {
        if (!rule.when || rule.when(ctx)) return { ...acc, ...rule.lock };
        return acc;
    }, {});

export const useLock = (): LockConfig | null => {
    const {
        selectedRow,
        stageTerritoryGeometry,
        buildingTerritoryGeometry,
        convertedGeometry,
        uploadedFiles
    } = useAppSelector(s => s.masterUtilities);
    const userRoles = useAppSelector(s => s.user.user?.roles ?? []);

    if (!selectedRow) return null;

    const primaryRole = userRoles.find(
        r => r === Roles.Contractor || r === Roles.DepartmentSpecialist,
    );
    if (!primaryRole) return null;

    const workPermitExpired = Boolean(
        selectedRow.expireDate &&
        new Date(selectedRow.expireDate).getTime() < Date.now(),
    );

    const buildingPermitValid = hasValidBuildingPermit(selectedRow)

    const stageKey = getStageKey(selectedRow.stage)

    const hasStageTerritory = Boolean(stageTerritoryGeometry?.[stageKey]);
    const hasBuildingTerritory = Boolean(buildingTerritoryGeometry?.[stageKey]);

    const hasFiles = Boolean(convertedGeometry && Object.keys(convertedGeometry).length);

    const hasUploadedFiles = Boolean(uploadedFiles?.[stageKey]?.length);

    const ctx: LockContext = {
        role: primaryRole,
        stage: selectedRow.stage,
        status: selectedRow.status as ProjectStatus,
        workPermitExpired,
        hasFiles,
        hasUploadedFiles,
        hasStageTerritory,
        hasBuildingTerritory,
        buildingPermitValid,
    } as LockContext;

    const rules = primaryRole === Roles.Contractor ? contractorRules : specialistRules;
    return applyRules(rules, ctx);
};
